import { subscribeSession } from './auth';
import { API_BASE } from './config';
import { inspectFile, type FileInfo, type FileKind } from './fileKind';
import {
  UniverseApiError,
  type UniverseAsset,
  type UniverseAssetDetail,
  type UniverseAssetType,
  type UniverseCollection,
  type UniversePage,
  type UniverseSearchResult,
} from './types';

/**
 * Client for /api/universe/v1. The Universe server answers it, sorting and
 * proxying to stream-sansar with the viewer's credentials from the session
 * cookie, so no token is ever attached here.
 */
const UNIVERSE_PATH = '/api/universe/v1';

/** Backend returns root-relative file URLs; they must point at the backend origin. */
export function resolveBackendUrl(url: string | null): string | null {
  if (!url) return null;
  if (/^https?:\/\//i.test(url)) return url;
  return url.startsWith('/') ? `${API_BASE}${url}` : url;
}

interface Envelope<T> {
  success?: boolean;
  message?: string;
  data?: T;
}

async function get<T>(path: string, params?: Record<string, string | number | null | undefined>, signal?: AbortSignal): Promise<T> {
  const qs = new URLSearchParams();
  for (const [key, value] of Object.entries(params ?? {})) {
    if (value !== null && value !== undefined && value !== '') qs.set(key, String(value));
  }
  const query = qs.toString();
  const res = await fetch(`${API_BASE}${UNIVERSE_PATH}${path}${query ? `?${query}` : ''}`, {
    credentials: 'include',
    headers: { Accept: 'application/json' },
    signal,
  });

  let body: Envelope<T> | null = null;
  try {
    body = (await res.json()) as Envelope<T>;
  } catch {
    body = null;
  }
  if (!res.ok || body?.success === false || body?.data === undefined) {
    throw new UniverseApiError(body?.message ?? 'Request failed', res.status || 500);
  }
  return body.data;
}

function withResolvedUrls<T extends UniverseAsset>(asset: T): T {
  return {
    ...asset,
    thumbnail_url: resolveBackendUrl(asset.thumbnail_url),
    cover_url: resolveBackendUrl(asset.cover_url),
    preview_url: resolveBackendUrl(asset.preview_url),
    playback_url: resolveBackendUrl(asset.playback_url),
  };
}

/** Credentials go only to this site and the backend, never to a third-party file host. */
function fileRequestInit(url: string): RequestInit {
  const origin = new URL(url, window.location.href).origin;
  const trusted = origin === window.location.origin || (API_BASE !== '' && origin === new URL(API_BASE).origin);
  return { credentials: trusted ? 'include' : 'omit' };
}

const fileInfoCache = new Map<string, Promise<FileInfo>>();

function inspect(url: string, nameHint: string): Promise<FileInfo> {
  const cached = fileInfoCache.get(url);
  if (cached) return cached;
  const request = inspectFile(url, fileRequestInit(url), nameHint);
  fileInfoCache.set(url, request);
  return request;
}

/** The asset type a detected file should be labelled with, when the backend's label is too coarse. */
const TYPE_FOR_KIND: Partial<Record<FileKind, UniverseAssetType>> = {
  pdf: 'pdf',
  audio: 'audio',
  video: 'video',
  sheet: 'spreadsheet',
  pptx: 'presentation',
};

const KIND_FOR_TYPE: Partial<Record<UniverseAssetType, FileKind>> = {
  pdf: 'pdf',
  audio: 'audio',
  video: 'video',
  spreadsheet: 'sheet',
  presentation: 'pptx',
};

/**
 * The backend maps file types it doesn't recognise (MP3, WAV, M4A…) to `document`, and only some
 * documents come with a rendered `preview_url`. Anything downloadable without one is inspected so
 * the viewer can preview the file itself.
 */
async function withFileInfo(asset: UniverseAssetDetail): Promise<UniverseAssetDetail> {
  if (asset.type === 'link' || asset.preview_url || !asset.file_url) return asset;
  if ((asset.type === 'video' || asset.type === 'audio') && asset.playback_url) return asset;
  const inspected = await inspect(asset.file_url, asset.title);
  // When the file itself gave nothing away, the backend's specific labels are still a good guess.
  const guessed = inspected.kind === 'unknown' ? KIND_FOR_TYPE[asset.type] : undefined;
  const file = guessed ? { ...inspected, kind: guessed } : inspected;
  const promoted = asset.type === 'document' ? TYPE_FOR_KIND[file.kind] : undefined;
  return { ...asset, type: promoted ?? asset.type, file };
}

/** Fetches a whole file with the viewer's credentials, for previews rendered in the browser. */
export async function fetchFile(url: string, signal?: AbortSignal): Promise<Blob> {
  const res = await fetch(url, { ...fileRequestInit(url), signal });
  if (!res.ok) throw new UniverseApiError('File request failed', res.status);
  return res.blob();
}

const detailCache = new Map<string, Promise<UniverseAssetDetail>>();

// Access tiers are decided per viewer, so cached details go stale on sign-in/out.
subscribeSession(() => detailCache.clear());

export const universeApi = {
  async listAssets(opts: { sort: string; cursor?: string | null; limit?: number }, signal?: AbortSignal): Promise<UniversePage> {
    const page = await get<UniversePage>('/assets', { sort: opts.sort, cursor: opts.cursor, limit: opts.limit }, signal);
    return { ...page, items: page.items.map(withResolvedUrls) };
  },

  async search(opts: { q: string; sort?: string; limit?: number }, signal?: AbortSignal): Promise<UniverseSearchResult> {
    const result = await get<UniverseSearchResult>('/search', { q: opts.q, sort: opts.sort, limit: opts.limit }, signal);
    return { ...result, items: result.items.map(withResolvedUrls) };
  },

  async getCollection(
    opts: { slug: string; sort?: string; cursor?: string | null; limit?: number },
    signal?: AbortSignal
  ): Promise<UniversePage> {
    const page = await get<UniversePage>(
      `/collections/${encodeURIComponent(opts.slug)}`,
      { sort: opts.sort, cursor: opts.cursor, limit: opts.limit },
      signal
    );
    return { ...page, items: page.items.map(withResolvedUrls) };
  },

  async listCollections(signal?: AbortSignal): Promise<UniverseCollection[]> {
    const data = await get<{ items: UniverseCollection[] }>('/collections', undefined, signal);
    return data.items;
  },

  /** Cached so hover prefetch makes opening instant; failures are evicted. */
  getAsset(id: string): Promise<UniverseAssetDetail> {
    const cached = detailCache.get(id);
    if (cached) return cached;
    const request = get<UniverseAssetDetail>(`/assets/${encodeURIComponent(id)}`).then((asset) =>
      withFileInfo({
        ...withResolvedUrls(asset),
        file_url: resolveBackendUrl(asset.file_url),
      })
    );
    detailCache.set(id, request);
    request.catch(() => detailCache.delete(id));
    return request;
  },

  prefetchAsset(id: string): void {
    universeApi.getAsset(id).catch(() => undefined);
  },
};
